import React, { useMemo } from "react";
import TreeSelect from "../TreeSelect/TreeSelect";
import { transformToTree } from "../TreeSelect/utils";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import { HgProductAttribute } from "../../../types/taxonomy";
import { SELECTION_PRESETS } from "../TreeSelect/hooks/useHierarchicalSelection";

interface ProductAttributesInputProps {
  onProductAttributesSelect: (productAttributesIds: string[]) => void;
  selectedItems: string[];
  id?: string;
}

// Flatten the nested attribute structure and create proper parent-child relationships
const processAttributes = (attributes: HgProductAttribute[]) => {
  const result: Array<{
    id: string;
    parentId: string;
    name: string;
    level: number;
    description: string | null;
  }> = [];

  // Process top-level attributes
  attributes.forEach((attr) => {
    // Add the top-level attribute with parentId "0"
    result.push({
      id: attr.id.toString(),
      parentId: "0",
      name: attr.name,
      level: attr.level,
      description: attr.description,
    });

    // Process children attributes with parent reference
    attr.childrenAttributes.forEach((child) => {
      result.push({
        id: child.id.toString(),
        parentId: attr.id.toString(),
        name: child.name,
        level: child.level,
        description: child.description,
      });
    });
  });

  return result;
};

export const ProductAttributesInput: React.FC<ProductAttributesInputProps> = ({
  onProductAttributesSelect,
  selectedItems = [],
  id = "product-attributes-input",
}) => {
  const { productAttributes } = useTaxonomyContext();

  // Process and flatten the attributes data
  const processedAttributes = useMemo(
    () => processAttributes(productAttributes),
    [productAttributes]
  );

  const allOptions = useMemo(
    () => transformToTree(processedAttributes, (item) => item.name),
    [processedAttributes]
  );

  return (
    <TreeSelect
      id={id}
      items={allOptions}
      selectedItems={selectedItems}
      onSelectItems={onProductAttributesSelect}
      allExpanded={true}
      selectionRules={SELECTION_PRESETS.flexible}
      showItemCounts
    />
  );
};
